import React, { useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ArrowRight, FlaskConical, Search, X } from 'lucide-react';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { BiomarkerGroup } from '../components/BiomarkerGroup';
import { PageHeader } from '../components/PageHeader';
import { StatusMarker } from '../components/StatusMarker';
import { usePlatform } from '../contexts/PlatformContext';
import { biomarkerCategories, labReport } from '../data/biomarkers';
import { getStatus } from '../utils/biomarkers';
type StatusFilter = 'all' | 'attention' | 'optimal';
const statusFilters: {
  id: StatusFilter;
  label: string;
}[] = [{
  id: 'all',
  label: 'All'
}, {
  id: 'attention',
  label: 'Needs attention'
}, {
  id: 'optimal',
  label: 'In range'
}];
export function Results() {
  const {
    goal,
    stage,
    panel
  } = usePlatform();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [category, setCategory] = useState('All');
  const flagged = useMemo(() => panel.filter(b => getStatus(b) !== 'optimal'), [panel]);
  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = panel.filter(b => {
      const s = getStatus(b);
      if (category !== 'All' && b.category !== category) return false;
      if (statusFilter === 'attention' && s === 'optimal') return false;
      if (statusFilter === 'optimal' && s !== 'optimal') return false;
      return !q || b.name.toLowerCase().includes(q);
    });
    return biomarkerCategories.map(c => ({
      name: c,
      total: panel.filter(b => b.category === c).length,
      items: filtered.filter(b => b.category === c).sort((a, b) => Number(getStatus(a) === 'optimal') - Number(getStatus(b) === 'optimal'))
    })).filter(g => g.items.length > 0);
  }, [panel, query, statusFilter, category]);
  if (!goal) return <Navigate to="/profile" replace data-id="element-251" data-mp="e4cq537bvr0f" />;
  const inRangePct = Math.round((panel.length - flagged.length) / panel.length * 100);
  const focusAreas = biomarkerCategories.map(c => ({
    name: c,
    count: flagged.filter(b => b.category === c).length
  })).filter(f => f.count > 0);
  const hasFilters = query || statusFilter !== 'all' || category !== 'All';
  return <div data-id="element-252" data-mp="3qncayk57ojlb">
      <PageHeader eyebrow="Blood results" title={`${panel.length} biomarkers, one clear picture`} description={goal === 'pregnancy' ? 'Read against pregnancy reference ranges, so we focus on what matters for you and your baby.' : 'Heart, metabolism, hormones and recovery — everything behind your wellbeing pack.'} actions={<Button size="lg" className="bg-[#9b8cf0] text-[#0f1017] hover:bg-[#b0a4f5]" onClick={() => navigate('/plan')} data-id="element-254" data-mp="1ftzs5bniznqj">
            See my recommendation <ArrowRight className="size-4" data-id="element-255" data-mp="1if7ey9zed4zd" />
          </Button>} data-id="element-253" data-mp="3qo47pyajond6" />

      <div className="mt-10 grid gap-4 lg:grid-cols-[280px_1fr]" data-id="element-256" data-mp="1id3ddyoz0i29">
        <div className="flex items-center gap-5 rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-id="element-257" data-mp="1ncfzuibxjn4b">
          <div className="relative size-20 shrink-0 rounded-full" style={{
          background: `conic-gradient(#8fd3b0 ${inRangePct * 3.6}deg, rgba(255,255,255,0.08) 0deg)`
        }} role="img" aria-label={`${inRangePct}% of markers in range`} data-id="element-258" data-mp="1s592n0nu8ldr">
            <div className="absolute inset-2 flex items-center justify-center rounded-full bg-[#0f1017] text-lg font-medium" data-id="element-259" data-mp="14fol8m7c1w6l">
              {inRangePct}%
            </div>
          </div>
          <div data-id="element-260" data-mp="1eedvptwwgect">
            <p className="font-medium" data-id="element-261" data-mp="1eexax2v60on5">{panel.length - flagged.length} in range</p>
            <p className="text-sm text-[#f5b454]" data-id="element-262" data-mp="2ksmu4j8rooqh">{flagged.length} need attention</p>
            <div className="mt-2" data-id="element-263" data-mp="2prpzy5wkmyzi">
              <StatusMarker goal={goal} stage={stage} size="sm" data-id="element-264" data-mp="2pqodl5a3uq2h" />
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-id="element-265" data-mp="2nfxbmewqknad">
          <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-266" data-mp="2jdtzdo28teo1">
            <p className="font-medium" data-id="element-267" data-mp="2xd20wnlvoyve">Focus areas</p>
            <p className="flex items-center gap-2 text-xs text-muted-foreground" data-id="element-268" data-mp="2scxhaopobo0y">
              <FlaskConical className="size-3.5 text-[#9b8cf0]" data-id="element-269" data-mp="2sf0cro9li9d2" />
              {labReport.lab} · {labReport.date}
            </p>
          </div>
          <div className="mt-4 flex flex-wrap gap-2" data-id="element-270" data-mp="32bwpk66j1hnf">
            {focusAreas.map(f => <button key={f.name} onClick={() => {
            setCategory(f.name);
            setStatusFilter('attention');
          }} className="flex items-center gap-2 rounded-full border border-[#f5b454]/25 bg-[#f5b454]/[0.06] px-3 py-1.5 text-sm transition-colors hover:border-[#f5b454]/50" data-id="element-271" data-mp="3g31loyaorrt0">
                {f.name}
                <span className="font-mono text-[11px] text-[#f5b454]" data-id="element-272" data-mp="8hqstbehl8op">{f.count}</span>
              </button>)}
          </div>
        </div>
      </div>

      <div className="sticky top-[65px] z-10 -mx-6 mt-8 border-b border-white/10 bg-[#0f1017]/90 px-6 py-4 backdrop-blur" data-id="element-273" data-mp="3up7ao4hmaive">
        <div className="flex flex-col gap-3 md:flex-row md:items-center" data-id="element-274" data-mp="3ie7iv39k9o8">
          <div className="relative md:w-72" data-id="element-275" data-mp="3kzfc6z6ed5ln">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" data-id="element-276" data-mp="3kym1xo88bxyr" />
            <Input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search biomarkers" aria-label="Search biomarkers" className="h-10 bg-white/5 pl-9" data-id="element-277" data-mp="3prf8mhn9dgdh" />
          </div>
          <div className="flex gap-1 rounded-full border border-white/10 p-1" role="group" aria-label="Filter by status" data-id="element-278" data-mp="321kw616upttr">
            {statusFilters.map(f => <button key={f.id} aria-pressed={statusFilter === f.id} onClick={() => setStatusFilter(f.id)} className={`rounded-full px-3 py-1 text-sm transition-colors ${statusFilter === f.id ? 'bg-white/10 text-foreground' : 'text-muted-foreground hover:text-foreground'}`} data-id="element-279" data-mp="2nurcioy6qovt">
                {f.label}
              </button>)}
          </div>
          <label className="sr-only" htmlFor="category" data-id="element-280" data-mp="36rst7713qdfl">Category</label>
          <select id="category" value={category} onChange={e => setCategory(e.target.value)} className="h-10 rounded-md border border-white/10 bg-white/5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#9b8cf0]" data-id="element-281" data-mp="31w3qiutscgf2">
            <option value="All" className="bg-[#0f1017]" data-id="element-282" data-mp="1lhloi6lljibz">All categories</option>
            {biomarkerCategories.map(c => <option key={c} value={c} className="bg-[#0f1017]" data-id="element-283" data-mp="1gg4p6firg3xi">{c}</option>)}
          </select>
          {hasFilters && <Button variant="ghost" size="sm" onClick={() => {
          setQuery('');
          setStatusFilter('all');
          setCategory('All');
        }} data-id="element-284" data-mp="y8h6du5y3y2">
              <X className="size-3.5" data-id="element-285" data-mp="ap32nfzl4zig" /> Clear
            </Button>}
        </div>
      </div>

      <div className="mt-6 space-y-4" data-id="element-286" data-mp="fmmhoi43xgbg">
        {groups.length === 0 ? <div className="rounded-2xl border border-dashed border-white/10 p-12 text-center" data-id="element-287" data-mp="jo834rupj2x9">
            <p className="font-medium" data-id="element-288" data-mp="onkkwy6q1jsz">No biomarkers match</p>
            <p className="mt-1 text-sm text-muted-foreground" data-id="element-289" data-mp="om9l19gecw75">Try a different search or clear your filters.</p>
          </div> : groups.map(g => <BiomarkerGroup key={g.name} category={g.name} items={g.items} totalInCategory={g.total} data-id="element-290" data-mp="2qgqz7aaosq0q" />)}
      </div>
    </div>;
}